import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { apiHandler } from '../../../api/apihandler';
import { CHAT_USERS, GET_CONNECTED_USERS } from '../../../api/constants';
import { apiSucceeded, getAvatarColor, getInitials } from '../feed/feedUtils';
import { user_data as userDataSelector } from '../../../store/setting/selectors.ts';
import { NO_IMAGE, getCurrentUser, fullName, formatChatTime } from './chatUtils';
import EmptyState from '../../../components/nemonto/EmptyState';
import '../social/social-list-page.css';
import './chat-page.css';

const EntryAvatar = ({ photo, name }) => {
  if (photo) return <img src={photo} alt={name} className="nmcp-avatar" />;
  return (
    <div className="nmcp-avatar" style={{ background: getAvatarColor(name || 'User') }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

export const ChatEmptyPanel = () => (
  <div className="nmcp-panel-empty">
    <EmptyState icon="forum" title="Select a conversation" sub="Pick a chat from the list or start a new one." />
  </div>
);

// Builds a placeholder row for a conversation the sidebar doesn't know
// about yet - happens the first time you open a group/DM that has no
// messages, since the backend's chat_users list only includes threads that
// already have at least one message in them.
function buildSyntheticEntry(type, id, header) {
  if (type === 'group') {
    return {
      id: `synthetic-group-${id}`,
      group_id: id,
      user_group: { name: header?.title || 'Group Chat', image: header?.avatarUrl || null },
      unread_count: 0,
      user_chat: null,
    };
  }
  return {
    id: `synthetic-user-${id}`,
    group_id: null,
    member_id: Number(id),
    member: { first_name: header?.title || 'Chat', last_name: '', user_setting: { photo: header?.avatarUrl || null } },
    unread_count: 0,
    user_chat: null,
  };
}

const ChatPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const userDataRaw = useSelector(userDataSelector);
  const currentUser = getCurrentUser(userDataRaw);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const inConversation = !!(params?.type && params?.id);

  // Read inside socket callbacks via paramsRef.current so the handlers
  // (registered once, for the life of the socket) always know which
  // conversation is currently open instead of closing over a stale value.
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  });

  const socketRef = useRef(null);
  const joinedGroupsRef = useRef(new Set());

  useEffect(() => {
    loadEntries();
  }, []);

  // One socket for the whole chat section, alive for as long as the user
  // is anywhere under /chat - this is what lets the sidebar (last message,
  // ordering, unread badges) update instantly even for conversations that
  // aren't the one currently open. The open conversation additionally runs
  // its own socket (ChatConversationPage) to stream messages into the
  // active thread; the two update different pieces of UI so both can react
  // to the same server event without conflicting.
  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_BASE_URL);
    socketRef.current = socket;

    socket.on('connect', () => {
      if (currentUser?.id) socket.emit('join', currentUser.id);
      joinedGroupsRef.current.forEach((groupId) => socket.emit('joinRoom', groupId));
    });

    socket.on('privateMessage', (incoming) => {
      const chat = incoming?.chat || incoming;
      if (!chat) return;
      const meId = Number(currentUser?.id);
      const otherId = Number(chat.sender_id) === meId ? Number(chat.receiver_id) : Number(chat.sender_id);
      const isOpen = paramsRef.current?.type === 'user' && String(paramsRef.current?.id) === String(otherId);
      const isIncoming = Number(chat.sender_id) !== meId;

      setEntries((prev) => {
        const idx = prev.findIndex((e) => !e.group_id && String(e.member_id) === String(otherId));
        const lastMessage = { message: chat.message, created_at: chat.created_at };

        if (idx === -1) {
          if (!isIncoming) return prev;
          return [
            {
              id: `dm-${otherId}`,
              group_id: null,
              member_id: otherId,
              member: chat.sender,
              user_chat: lastMessage,
              unread_count: isOpen ? 0 : 1,
            },
            ...prev,
          ];
        }

        const updated = {
          ...prev[idx],
          user_chat: lastMessage,
          unread_count: isOpen ? 0 : (prev[idx].unread_count || 0) + (isIncoming ? 1 : 0),
        };
        return [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
      });
    });

    socket.on('groupMessage', (incoming) => {
      const chat = incoming?.chat || incoming;
      if (!chat?.group_id) return;
      const meId = Number(currentUser?.id);
      const isOpen = paramsRef.current?.type === 'group' && String(paramsRef.current?.id) === String(chat.group_id);
      const isIncoming = Number(chat.sender_id) !== meId;

      setEntries((prev) => {
        const idx = prev.findIndex((e) => String(e.group_id) === String(chat.group_id));
        if (idx === -1) return prev;

        const updated = {
          ...prev[idx],
          user_chat: { message: chat.message, created_at: chat.created_at },
          unread_count: isOpen ? 0 : (prev[idx].unread_count || 0) + (isIncoming ? 1 : 0),
        };
        return [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
      });
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  // Join every group's room as soon as we know about it (initial load and
  // any newly-appearing group), so groupMessage events arrive even while
  // browsing a different conversation or sitting on the list.
  useEffect(() => {
    const socket = socketRef.current;
    entries.forEach((e) => {
      if (!e.group_id || joinedGroupsRef.current.has(e.group_id)) return;
      joinedGroupsRef.current.add(e.group_id);
      socket?.emit('joinRoom', e.group_id);
    });
  }, [entries]);

  // Whenever the open conversation changes (or the entries list itself
  // reloads/updates), make sure it's represented in the sidebar and that
  // its unread badge is cleared:
  //  - if it's already in the list, just clear the unread count, in step
  //    with the read-all call ChatConversationPage fires on mount.
  //  - if it's not there yet (first message never sent, so the backend's
  //    chat_users list doesn't know about it), add a placeholder row built
  //    from the header info passed via navigate(..., {state}) so the group
  //    or person is visible in the list right away instead of only after a
  //    reload. A later loadEntries() (or the real entry arriving over the
  //    socket) naturally replaces/reconciles this placeholder.
  useEffect(() => {
    if (!params?.type || !params?.id) return;
    setEntries((prev) => {
      const idx = prev.findIndex((e) =>
        params.type === 'group' ? String(e.group_id) === String(params.id) : !e.group_id && String(e.member_id) === String(params.id),
      );

      if (idx === -1) {
        return [buildSyntheticEntry(params.type, params.id, location.state?.header), ...prev];
      }
      if (!prev[idx].unread_count) return prev;

      const updated = { ...prev[idx], unread_count: 0 };
      return [...prev.slice(0, idx), updated, ...prev.slice(idx + 1)];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.type, params?.id, entries]);

  async function loadEntries() {
    setLoading(true);
    try {
      const res = await apiHandler(CHAT_USERS, 'POST', { page: 1, limit: 100 });
      if (apiSucceeded(res)) setEntries(res?.data?.data?.chat_users?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function runSearch(value) {
    setSearchText(value);
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await apiHandler(GET_CONNECTED_USERS, 'POST', { search_text: value });
      if (apiSucceeded(res)) setSearchResults(res?.data?.data?.users?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
    }
  }

  function closeSearch() {
    setSearchOpen(false);
    setSearchText('');
    setSearchResults([]);
  }

  function openEntry(entry) {
    closeSearch();
    if (entry?.group_id) {
      navigate(`/chat/group/${entry.group_id}`, {
        state: { header: { title: entry?.user_group?.name || 'Group Chat', avatarUrl: entry?.user_group?.image, isGroup: true } },
      });
    } else {
      navigate(`/chat/user/${entry?.member_id}`, {
        state: { header: { title: fullName(entry?.member), avatarUrl: entry?.member?.user_setting?.photo, isGroup: false } },
      });
    }
  }

  function openNewChat(user) {
    closeSearch();
    navigate(`/chat/user/${user.id}`, {
      state: { header: { title: fullName(user), avatarUrl: user?.user_setting?.photo, isGroup: false } },
    });
  }

  const isEntryActive = (entry) => {
    if (!inConversation) return false;
    if (entry?.group_id) return params.type === 'group' && String(params.id) === String(entry.group_id);
    return params.type === 'user' && String(params.id) === String(entry.member_id);
  };

  return (
    <div className={`nmcp-shell ${inConversation ? 'nmcp-show-panel' : 'nmcp-show-list'}`}>
      <div className="nmcp-sidebar">
        <div className="nmcp-sidebar-header">
          <h3 className="nmcp-sidebar-title">Chat</h3>
          <div className="nmci-search-wrap">
            <span className="material-symbols-outlined nmci-search-icon">search</span>
            <input
              type="text"
              className="nmci-search-input"
              placeholder="Start a new chat…"
              value={searchText}
              onFocus={() => setSearchOpen(true)}
              onChange={(e) => runSearch(e.target.value)}
            />
          </div>
          {searchOpen && searchText.trim() && (
            <div className="nmci-search-results">
              {searching && <div className="nmci-search-empty">Searching…</div>}
              {!searching && searchResults.length === 0 && <div className="nmci-search-empty">No users found.</div>}
              {!searching &&
                searchResults.map((u) => (
                  <div key={u.id} className="nmci-search-result" onClick={() => openNewChat(u)}>
                    <EntryAvatar photo={u?.user_setting?.photo} name={fullName(u)} />
                    <div className="nmsl-body">
                      <div className="nmsl-name">{fullName(u)}</div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="nmcp-list">
          {loading && <p className="nmsl-loading">Loading…</p>}
          {!loading && entries.length === 0 && (
            <EmptyState icon="chat" title="No conversations yet" sub="Start a new chat above or open a group to say hello." />
          )}

          {!loading &&
            entries.map((entry) => {
              const isGroup = !!entry?.group_id;
              const title = isGroup ? entry?.user_group?.name : fullName(entry?.member);
              const avatarUrl = isGroup ? entry?.user_group?.image : entry?.member?.user_setting?.photo;
              const lastMessage = entry?.user_chat?.message || (isGroup ? 'Say hello to the group' : 'Say hello');
              const unread = entry?.unread_count || 0;

              return (
                <div
                  key={`${isGroup ? 'g' : 'u'}-${entry.id}`}
                  className={`nmcp-entry ${isEntryActive(entry) ? 'active' : ''}`}
                  onClick={() => openEntry(entry)}
                >
                  <EntryAvatar photo={avatarUrl || NO_IMAGE} name={title} />
                  <div className="nmcp-entry-body">
                    <div className="nmcp-entry-name">
                      {title}
                      {isGroup && <span className="nmci-group-tag">Group</span>}
                    </div>
                    <div className="nmcp-entry-excerpt">{lastMessage}</div>
                  </div>
                  <div className="nmcp-entry-meta">
                    {entry?.user_chat?.created_at && <div className="nmcp-entry-time">{formatChatTime(entry.user_chat.created_at)}</div>}
                    {unread > 0 && <div className="nmcp-unread-badge">{unread}</div>}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <div className="nmcp-panel">
        <Outlet key={`${params.type || ''}/${params.id || ''}`} />
      </div>
    </div>
  );
};

export default ChatPage;
